import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Searchable picker. `options` = [{ value, label, hint }].
// Uses OWN controlled filtering (shouldFilter=false) so multi-character / multi-word /
// case-insensitive / paste search works reliably — Radix Select typeahead and cmdk's
// default scorer both misbehave with long product lists (first-char-only symptom).
export function Combobox({
  options = [], value, onChange, placeholder = "Select…",
  emptyText = "No match.", testId, className, disabled,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 200);
    const tokens = q.split(/\s+/);
    return options
      .filter((o) => {
        const hay = `${o.label} ${o.hint || ""}`.toLowerCase();
        return tokens.every((t) => hay.includes(t));
      })
      .slice(0, 200);
  }, [options, query]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" disabled={disabled}
          data-testid={testId}
          className={cn("h-auto min-h-9 w-full justify-between py-1.5 text-sm font-normal", !selected && "text-muted-foreground", className)}
        >
          <span className="whitespace-normal break-words text-left leading-tight line-clamp-2">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] bg-white p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder} className="h-9"
            value={query} onValueChange={setQuery}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filtered.map((o) => (
                <CommandItem
                  key={o.value} value={o.value}
                  onSelect={() => { onChange(o.value); setOpen(false); setQuery(""); }}
                  data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex min-w-0 flex-col">
                    <span className="whitespace-normal break-words leading-tight">{o.label}</span>
                    {o.hint && <span className="text-xs text-muted-foreground">{o.hint}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
